import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import Payment from 'src/smoothr-web-app-core/models/Payment';
import Customer from 'src/smoothr-web-app-core/models/Customer';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import { environment } from '../../../environments/environment';
import { Api } from 'src/smoothr-web-app-core/api/api';
import { TranslateService } from '@ngx-translate/core';

const GP_SCRIPT = 'https://js.globalpay.com/4.1.3/globalpayments.js';
const THREE_DS_SCRIPT = 'assets/globalpayments-3ds.min.js';
const ESERVICE_BASE = environment.baseUrl + '/v3/payment';

interface EServiceSavedCard {
	token?: string;
	alias?: string;
	provider?: string;
	cardScheme?: string;
	brand?: string;
	cardNumber?: string;
	expiryDate?: string;
}

@Component({
	selector: 'app-eservice-credit-card',
	templateUrl: './eservice-credit-card.component.html',
	styleUrls: ['./eservice-credit-card.component.scss'],
})
export class EserviceCreditCardComponent implements OnInit, OnDestroy {
	@Input() payment: Payment;
	@Input() customer: Customer;
	@Input() venue: Venue;
	@Output() paymentSuccess = new EventEmitter<Payment>();
	@Output() paymentError = new EventEmitter<string>();

	statusMessage = '';
	paymentReference = '';
	authenticationId = '';
	showCardForm = false;
	isSubmitting = false;
	savedCards: EServiceSavedCard[] = [];
	selectedSavedCard: EServiceSavedCard | null = null;

	private cardFormRef: any = null;
	private initialized = false;

	constructor(
		private cdr: ChangeDetectorRef,
		private translate: TranslateService
	) {}

	ngOnInit() {
		this.statusMessage = this.translate.instant('eservice_cc.status.initializing');
		this.initialize();
	}

	ngOnDestroy() {
		this.initialized = false;
		this.cardFormRef = null;
	}

	private loadScript(src: string): Promise<void> {
		return new Promise((resolve, reject) => {
			if (document.querySelector(`script[src="${src}"]`)) {
				resolve();
				return;
			}
			const s = document.createElement('script');
			s.src = src;
			s.async = true;
			s.onload = () => resolve();
			s.onerror = () => reject(new Error(`Failed to load: ${src}`));
			document.body.appendChild(s);
		});
	}

	private extractAccessToken(payload: any): string {
		return (
			payload?.accessToken ||
			payload?.access_token ||
			payload?.token ||
			payload?.eServicePayment?.accessToken ||
			payload?.eServicePayment?.access_token ||
			''
		);
	}

	private async initialize() {
		try {
			const cards = await this.fetchSavedCards();
			this.savedCards = cards;
			this.cdr.detectChanges();

			if (cards.length > 0) {
				this.statusMessage = this.translate.instant('eservice_cc.status.select_card');
				return;
			}

			await this.showNewCardForm();
		} catch (e: any) {
			this.statusMessage = e?.message || this.translate.instant('eservice_cc.status.init_error');
			this.cdr.detectChanges();
		}
	}

	private async showNewCardForm() {
		const accessToken = this.extractAccessToken(this.payment);
		if (!accessToken) {
			this.statusMessage = this.translate.instant('eservice_cc.status.no_token');
			this.cdr.detectChanges();
			return;
		}
		this.showCardForm = true;
		this.statusMessage = this.translate.instant('eservice_cc.status.loading_form');
		this.cdr.detectChanges();
		await this.initializeHostedFields(accessToken);
	}

	private async initializeHostedFields(accessToken: string) {
		await this.loadScript(GP_SCRIPT);
		const gp = (window as any).GlobalPayments;
		if (!gp || this.initialized) {
			return;
		}

		gp.configure({ accessToken, apiVersion: '2021-03-22', env: environment.production ? 'production' : 'sandbox' });

		const cardForm = gp.ui.form({
			fields: {
				'card-number': { placeholder: '•••• •••• •••• ••••', target: '#card-number' },
				'card-expiration': { placeholder: 'MM / YYYY', target: '#card-expiration' },
				'card-cvv': { placeholder: '•••', target: '#card-cvv' },
				'card-holder-name': { placeholder: 'Jane Smith', target: '#card-holder-name' },
				submit: { text: 'Pay Now', target: '#submit-button', value: 'Pay Now' },
			},
			styles: {
				'input[type=text], input[type=tel]': {
					'margin-bottom': '14px',
					padding: '12px',
					border: '1px solid #e0e0e0',
					'border-radius': '8px',
					'font-size': '14px',
					'font-family': 'AppFont, sans-serif',
					color: '#000000',
				},
				'input[type=text]:focus-visible, input[type=tel]:focus-visible': {
					outline: 'none',
					border: '1px solid #71a761',
					'box-shadow': '0 0 3px 0 #71a76133 inset',
				},
				'button[type=button]': {
					'background-color': '#71a761',
					color: 'white',
					padding: '12px',
					border: 'none',
					'border-radius': '16px',
					cursor: 'pointer',
					'font-size': '18px',
					'font-family': 'AppFontBold, sans-serif',
					'text-transform': 'uppercase',
					width: '100%',
				},
				'button[type=button]:hover': {
					'background-color': '#639355',
				},
			},
		});

		this.cardFormRef = cardForm;
		this.initialized = true;

		cardForm.ready(() => {
			this.statusMessage = this.translate.instant('eservice_cc.status.enter_details');
			this.cdr.detectChanges();
		});

		cardForm.on('token-success', async (resp: any) => {
			const token = resp?.paymentReference || '';
			this.paymentReference = token;
			this.authenticationId = '';
			this.statusMessage = this.translate.instant('eservice_cc.status.tokenized');
			this.isSubmitting = true;
			this.cdr.detectChanges();

			try {
				await this.runThreeDSecure(token);
			} catch (e: any) {
				const msg = e?.message || this.translate.instant('eservice_cc.status.3ds_failed');
				this.statusMessage = msg;
				this.paymentError.emit(msg);
			} finally {
				this.isSubmitting = false;
				this.cdr.detectChanges();
			}
		});

		cardForm.on('token-error', () => {
			this.statusMessage = this.translate.instant('eservice_cc.status.tokenize_failed');
			this.isSubmitting = false;
			this.cdr.detectChanges();
		});
	}

	private async loadThreeDSecureScript(): Promise<void> {
		await this.loadScript(THREE_DS_SCRIPT);
		if ((window as any).GlobalPayments?.ThreeDSecure?.checkVersion) {
			return;
		}
		// Reload after hosted fields may have overwritten the GP object
		const stale = document.querySelector(`script[src="${THREE_DS_SCRIPT}"]`);
		if (stale) {
			stale.remove();
		}
		await this.loadScript(THREE_DS_SCRIPT);
		if (!(window as any).GlobalPayments?.ThreeDSecure?.checkVersion) {
			throw new Error('3D Secure library unavailable. Add assets/globalpayments-3ds.min.js to the project.');
		}
	}

	private async runThreeDSecure(paymentMethodId: string) {
		const paymentId = this.payment?._id;
		if (!paymentId) {
			throw new Error(this.translate.instant('eservice_cc.status.no_payment_id'));
		}

		this.statusMessage = this.translate.instant('eservice_cc.status.checking_3ds');
		this.cdr.detectChanges();

		await this.loadThreeDSecureScript();
		const { checkVersion, initiateAuthentication, ChallengeWindowSize } = (window as any).GlobalPayments.ThreeDSecure;
		const headers: Record<string, string> = { 'Content-Type': 'application/json' };
		if (Api.authToken) {
			headers['Authorization'] = `Bearer ${Api.authToken}`;
		}

		const country = (this.venue?.country || 'DE').toUpperCase();
		const currency = this.venue?.currency || 'EUR';

		const versionData = await checkVersion(
			`${ESERVICE_BASE}/e-service/3ds/check-enrollment?webAppUrl=${encodeURIComponent(window.location.origin)}`,
			{ paymentId, paymentMethodId, card: { reference: paymentMethodId }, country, currency },
			headers
		);

		if (versionData.enrolled !== 'ENROLLED' && versionData.liabilityShift !== 'YES') {
			throw new Error(this.translate.instant('eservice_cc.status.not_enrolled'));
		}

		if (versionData.liabilityShift === 'YES') {
			this.authenticationId = versionData.serverTransactionId;
			await this.chargePayment(paymentMethodId, versionData.serverTransactionId);
			return;
		}

		this.statusMessage = this.translate.instant('eservice_cc.status.initiating_3ds');
		this.cdr.detectChanges();

		const authData = await initiateAuthentication(
			`${ESERVICE_BASE}/e-service/3ds/initiate`,
			{
				paymentId,
				paymentMethodId,
				versionCheckData: versionData,
				methodUrlComplete: true,
				card: { reference: paymentMethodId },
				country,
				currency,
				challengeWindow: {
					windowSize: ChallengeWindowSize?.Windowed600x400 || 'WINDOWED_600X400',
					displayMode: 'lightbox',
				},
			},
			headers
		);

		const authId =
			authData.serverTransactionId || authData.challenge?.response?.data?.threeDSServerTransID || versionData.serverTransactionId;

		if (
			authData.liabilityShift === 'YES' &&
			(authData.result === 'SUCCESS_AUTHENTICATED' || authData.result === 'SUCCESS_ATTEMPT_MADE')
		) {
			this.authenticationId = authId;
			await this.chargePayment(paymentMethodId, authId);
			return;
		}

		if (authData.status === 'CHALLENGE_REQUIRED') {
			this.authenticationId = authId;
			this.statusMessage = this.translate.instant('eservice_cc.status.challenge');
			this.cdr.detectChanges();
			await this.chargePayment(paymentMethodId, authId);
			return;
		}

		throw new Error(this.translate.instant('eservice_cc.status.3ds_incomplete'));
	}

	private async chargePayment(paymentMethodId: string, authId?: string) {
		this.statusMessage = this.translate.instant('eservice_cc.status.charging');
		this.cdr.detectChanges();

		const result = await Api.eServicePay(this.payment._id, paymentMethodId, authId);
		this.statusMessage = this.translate.instant('eservice_cc.status.success');
		this.cdr.detectChanges();
		this.paymentSuccess.emit({ ...this.payment, ...result });
	}

	private async fetchSavedCards(): Promise<EServiceSavedCard[]> {
		try {
			const uid = this.customer?.uid;
			if (!uid || this.customer?.isAnonymous) {
				return [];
			}
			const loyaltyUserId = `${environment.customerGroup}_${uid}`;
			return await Api.eServiceGetSavedCards(loyaltyUserId);
		} catch {
			return [];
		}
	}

	selectSavedCard(card: EServiceSavedCard) {
		this.selectedSavedCard = card;
		this.paymentReference = card.token || card.alias || '';
		this.authenticationId = '';
		this.showCardForm = false;
		this.statusMessage = this.translate.instant('eservice_cc.status.card_selected');
		this.cdr.detectChanges();
	}

	async useNewCard() {
		this.selectedSavedCard = null;
		this.paymentReference = '';
		this.authenticationId = '';
		this.initialized = false;
		this.cardFormRef = null;
		this.showCardForm = false;
		this.cdr.detectChanges();
		await this.showNewCardForm();
	}

	async chargeSelectedCard() {
		if (!this.paymentReference || this.isSubmitting) {
			return;
		}
		this.isSubmitting = true;
		this.cdr.detectChanges();
		try {
			await this.runThreeDSecure(this.paymentReference);
		} catch (e: any) {
			const msg = e?.message || 'Charge failed.';
			this.statusMessage = msg;
			this.paymentError.emit(msg);
		} finally {
			this.isSubmitting = false;
			this.cdr.detectChanges();
		}
	}

	savedCardLabel(card: EServiceSavedCard): string {
		return [card.cardScheme || card.brand || 'Card', card.cardNumber ? `****${card.cardNumber.slice(-4)}` : '', card.expiryDate || '']
			.filter(Boolean)
			.join('  ');
	}
}
